'use client';
import { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import { Heading } from './Heading';

type MarkdownProps = {
  content: string;
  onHeadingsChange?: (headings: Array<{ id: string; text: string; level: number }>) => void;
};

export function Markdown({ content, onHeadingsChange }: MarkdownProps) {
  const headings = useMemo(() => {
    const lines = content.split('\n');
    const found: Array<{ id: string; text: string; level: number }> = [];
    lines.forEach((line) => {
      const match = line.match(/^(#{2,6})\s+(.+)$/);
      if (match) {
        const level = match[1].length;
        const text = match[2].trim();
        const id = text
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-|-$/g, '');
        found.push({ id, text, level });
      }
    });
    onHeadingsChange?.(found);
    return found;
  }, [content, onHeadingsChange]);

  return (
    <div className="prose prose-slate dark:prose-invert max-w-none prose-headings:scroll-mt-24 prose-headings:font-semibold prose-headings:text-foreground prose-h1:text-2xl md:prose-h1:text-3xl prose-h2:text-xl md:prose-h2:text-2xl prose-h3:text-lg md:prose-h3:text-xl prose-h1:mt-8 prose-h2:mt-6 prose-h3:mt-4 prose-p:leading-relaxed prose-p:text-foreground prose-a:text-primary prose-a:no-underline hover:prose-a:underline prose-strong:text-foreground prose-code:text-sm prose-code:bg-muted prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-pre:bg-muted prose-pre:border prose-pre:border-border prose-pre:overflow-x-auto prose-blockquote:border-l-4 prose-blockquote:border-primary prose-blockquote:pl-4 prose-blockquote:italic prose-ul:list-disc prose-ol:list-decimal prose-li:my-1 break-words overflow-wrap-anywhere">
      <ReactMarkdown
        components={{
          h1: (props) => <Heading level={1} {...props} />,
          h2: (props) => <Heading level={2} {...props} />,
          h3: (props) => <Heading level={3} {...props} />,
          h4: (props) => <Heading level={4} {...props} />,
          h5: (props) => <Heading level={5} {...props} />,
          h6: (props) => <Heading level={6} {...props} />,
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}
